import api from './api';

// Employee master (PayrollProfile) - independent person records, not tied
// to the app's Employee table (see app/models.py's HR PAYROLL MODULE
// section docstring). `profileId` throughout is PayrollProfile.id.
export const getProfiles = (params = {}) => api.get('/payroll/profiles', { params });
export const getProfile = (profileId) => api.get(`/payroll/profiles/${profileId}`);
export const createProfile = (data) => api.post('/payroll/profiles', data);
export const updateProfile = (profileId, data) => api.put(`/payroll/profiles/${profileId}`, data);
export const deactivateProfile = (profileId) => api.delete(`/payroll/profiles/${profileId}`);

// Loans / advances ledger
export const getLoans = (params = {}) => api.get('/payroll/loans', { params });
export const createLoan = (data) => api.post('/payroll/loans', data);
export const updateLoan = (loanId, data) => api.put(`/payroll/loans/${loanId}`, data);
export const deleteLoan = (loanId) => api.delete(`/payroll/loans/${loanId}`);

// Leave Management (PL + Comp Off) - ledger-based, same pattern as loans.
// HR/CEO/System_Admin administered only, no employee self-service.
export const getLeaveBalances = (params = {}) => api.get('/payroll/leave/balances', { params });
export const getLeaveLedger = (params = {}) => api.get('/payroll/leave/ledger', { params });
export const grantLeave = (data) => api.post('/payroll/leave/grant', data);
export const grantAnnualPL = (payrollEmployeeIds, data = {}) =>
  api.post('/payroll/leave/grant-annual-pl', {
    ...data,
    payroll_employee_ids: payrollEmployeeIds?.length ? payrollEmployeeIds : undefined,
  });
export const recordLeaveUsage = (data) => api.post('/payroll/leave/usage', data);
export const deleteLeaveEntry = (entryId) => api.delete(`/payroll/leave/ledger/${entryId}`);

// Monthly attendance
export const getAttendance = (params) => api.get('/payroll/attendance', { params });
export const upsertAttendance = (data) => api.post('/payroll/attendance', data);
export const upsertAttendanceBulk = (records) => api.post('/payroll/attendance/bulk', { records });

// Payroll runs
export const getRuns = (params = {}) => api.get('/payroll/runs', { params });
export const getRun = (runId) => api.get(`/payroll/runs/${runId}`);
export const createRun = (data) => api.post('/payroll/runs', data);
export const calculateRun = (runId) => api.post(`/payroll/runs/${runId}/calculate`);
export const updateLine = (runId, lineId, data) => api.put(`/payroll/runs/${runId}/lines/${lineId}`, data);
export const finalizeRun = (runId) => api.post(`/payroll/runs/${runId}/finalize`);
export const markRunPaid = (runId) => api.post(`/payroll/runs/${runId}/mark-paid`);

// Payslip PDF - one combined PDF, one page per selected employee. Pass an
// array of PayrollProfile ids to generate for just those employees, or
// omit/pass an empty array for every calculated line in the run.
export const downloadPayslipsPdf = (runId, payrollEmployeeIds) =>
  api.get(`/payroll/runs/${runId}/payslips.pdf`, {
    params: payrollEmployeeIds?.length ? { payroll_employee_ids: payrollEmployeeIds.join(',') } : {},
    responseType: 'blob',
  });

// Old salary-sheet import (.xlsx, FACTORY/HO workbook shape). Note: no
// explicit Content-Type header on the multipart calls below - axios/the
// browser must set it themselves so the required multipart boundary gets
// attached; setting 'multipart/form-data' by hand here (a bug fixed
// 2026-09-11) produces a boundary-less Content-Type the backend's
// multipart parser rejects, which is what "some error" on upload was.
export const previewSheetImport = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/payroll/import/preview', formData);
};

export const commitSheetImport = (file, month, year, branch, updatePayStructure = false) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('month', month);
  formData.append('year', year);
  if (branch) formData.append('branch', branch);
  formData.append('update_pay_structure', updatePayStructure);
  return api.post('/payroll/import/commit', formData);
};

// One-time email backfill (.xlsx with Employee Code + Email columns) -
// only ever updates an existing profile's email, never creates one.
export const previewEmailImport = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/payroll/import/emails/preview', formData);
};

export const commitEmailImport = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/payroll/import/emails/commit', formData);
};

// Save each selected employee's payslip (PDF attached) as a draft email in
// the configured mailbox's Drafts folder - nothing is sent automatically,
// you review and click Send yourself. Omit/pass an empty array for every
// calculated line in the run.
export const draftPayslipsEmail = (runId, payrollEmployeeIds) =>
  api.post(`/payroll/runs/${runId}/draft-payslips`, {
    payroll_employee_ids: payrollEmployeeIds?.length ? payrollEmployeeIds : undefined,
  });

// Dashboard
export const getCostTrend = (params = {}) => api.get('/payroll/dashboard/cost-trend', { params });
export const getBranchSplit = (params) => api.get('/payroll/dashboard/branch-split', { params });
export const getAttendanceTrend = (params = {}) => api.get('/payroll/dashboard/attendance-trend', { params });
export const getAvgHoursByDepartment = (params) => api.get('/payroll/dashboard/avg-hours-by-department', { params });
